import React, {
  createContext,
  useContext,
  useState,
  useCallback,
  useMemo,
  useRef,
} from "react";
import { ENTITY_HANDLERS } from "../../components/entityHandlers";
import apiClient from "../../services/apiClient";

const AdminStateContext = createContext(null);

/**
 * Central state management hook for all admin operations
 * Uses direct handler registration instead of state triggers
 */
function useAdminStateManager() {
  // Core state
  const [selectedItems, setSelectedItems] = useState({
    groups: null,
    users: null,
  });

  const [hasChanges, setHasChanges] = useState({
    groups: false,
    users: false,
    permissions: false,
  });

  const [activeTab, setActiveTab] = useState("groups");

  const [isSaving, setIsSaving] = useState(false);
  const [isAdding, setIsAdding] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  // Store save handlers from child components
  const saveHandlers = useRef({
    groups: null,
    users: null,
    permissions: null,
  });

  // Permissions-specific state
  const [selectedGroupForPermissions, setSelectedGroupForPermissions] =
    useState(null);
  const [isPermissionsTabVisible, setIsPermissionsTabVisible] = useState(false);
  const [permissionsCache, setPermissionsCache] = useState({
    groupId: null,
    groupName: null,
    data: null,
  });

  // Remember last selections for tab restoration
  const [lastSelections, setLastSelections] = useState({
    groups: null,
    users: null,
  });

  // Track pending tab for unsaved changes modal
  const [pendingTabId, setPendingTabId] = useState(null);

  // ============= Computed Values =============

  const currentSelection = useMemo(() => {
    if (activeTab === "permissions") return selectedGroupForPermissions;
    return selectedItems[activeTab];
  }, [activeTab, selectedItems, selectedGroupForPermissions]);

  const currentHasChanges = useMemo(() => {
    return hasChanges[activeTab] || false;
  }, [activeTab, hasChanges]);

  // ============= Core Actions =============

  const selectItem = useCallback((tab, item) => {
    setSelectedItems((prev) => ({
      ...prev,
      [tab]: item,
    }));

    // Update last selection for this tab
    setLastSelections((prev) => ({
      ...prev,
      [tab]: item,
    }));
  }, []);

  const updateHasChanges = useCallback((tab, value) => {
    setHasChanges((prev) => ({
      ...prev,
      [tab]: value,
    }));
  }, []);

  // ============= Direct Save Handler Registration =============

  const registerSaveHandler = useCallback((tab, handler) => {
    saveHandlers.current[tab] = handler;
  }, []);

  const unregisterSaveHandler = useCallback((tab) => {
    saveHandlers.current[tab] = null;
  }, []);

  const executeSave = useCallback(async (tab) => {
    const handler = saveHandlers.current[tab];
    if (handler) {
      setIsSaving(true);
      try {
        await handler();
      } finally {
        setIsSaving(false);
      }
    }
  }, []);

  // ============= Tab Management =============

  const switchTab = useCallback(
    (newTab) => {
      // Restore last selection when switching back to a tab
      if (newTab === "groups" && lastSelections.groups) {
        setSelectedItems((prev) => ({
          ...prev,
          groups: lastSelections.groups,
        }));
      } else if (newTab === "users" && lastSelections.users) {
        setSelectedItems((prev) => ({
          ...prev,
          users: lastSelections.users,
        }));
      }

      setActiveTab(newTab);
      setPendingTabId(null);
    },
    [lastSelections, activeTab]
  );

  // ============= Item Operations =============

  const deleteItem = useCallback(
    async (tab, item, items, setItems) => {
      if (!item) return null;

      const entityHandler =
        tab === "groups" ? ENTITY_HANDLERS.Group : ENTITY_HANDLERS.User;

      try {
        setIsDeleting(true);

        // Find next item to select before deletion
        const currentIndex = items.findIndex((i) => i.id === item.id);
        let nextItem = null;

        if (items.length > 1) {
          if (currentIndex < items.length - 1) {
            nextItem = items[currentIndex + 1];
          } else if (currentIndex > 0) {
            nextItem = items[currentIndex - 1];
          }
        }

        // Call the delete API
        const deleteUrl = entityHandler.deleteItemURL(item.id);
        await apiClient.delete(deleteUrl);

        // Update the local state directly
        setItems((prev) => prev.filter((i) => i.id !== item.id));

        // Update selection
        selectItem(tab, nextItem);

        // Clear permissions if deleting the selected permissions group
        if (tab === "groups" && selectedGroupForPermissions?.id === item.id) {
          setSelectedGroupForPermissions(null);
          setIsPermissionsTabVisible(false);
          setPermissionsCache({ groupId: null, groupName: null, data: null });
        }

        return nextItem;
      } finally {
        setIsDeleting(false);
      }
    },
    [selectItem, selectedGroupForPermissions]
  );

  const addItem = useCallback(
    async (tab, items) => {
      const entityHandler =
        tab === "groups" ? ENTITY_HANDLERS.Group : ENTITY_HANDLERS.User;
      const itemType = tab === "groups" ? "group" : "user";

      // Generate unique name
      const newName = entityHandler.generateNewItemName(items);

      try {
        setIsAdding(true);

        // Create on server immediately
        const apiData = entityHandler.createApiDataForImmediate(newName);
        const createUrl = entityHandler.createItemURL();
        const response = await apiClient.post(createUrl, apiData);

        if (!response.data || response.data === undefined) {
          throw new Error(
            `Server did not return a valid ID for the new ${itemType}`
          );
        }

        // Create local item with server ID
        const localItem = entityHandler.createLocalItemFromServer(
          response.data,
          newName
        );

        // Clear permissions for new groups
        if (tab === "groups") {
          setPermissionsCache({
            groupId: null,
            groupName: null,
            data: null,
          });
          setSelectedGroupForPermissions(null);
          setIsPermissionsTabVisible(false);
        }

        return localItem;
      } finally {
        setIsAdding(false);
      }
    },
    [permissionsCache]
  );

  // ============= Permissions Management =============

  const showPermissionsForGroup = useCallback(
    (group) => {
      // Always clear cache for fresh data
      setPermissionsCache({ groupId: null, groupName: null, data: null });
      setSelectedGroupForPermissions(group);
      setIsPermissionsTabVisible(true);
      setActiveTab("permissions");
    },
    [permissionsCache]
  );

  const requestGroupChange = useCallback((newGroup) => {
    setSelectedGroupForPermissions(newGroup);
    setPermissionsCache({ groupId: null, groupName: null, data: null });
  }, []);

  // ============= Return Value =============

  return {
    // State
    selectedItems,
    hasChanges,
    activeTab,
    isSaving,
    isAdding,
    isDeleting,
    currentSelection,
    currentHasChanges,
    selectedGroupForPermissions,
    isPermissionsTabVisible,
    permissionsCache,
    pendingTabId,

    // Actions
    selectItem,
    updateHasChanges,
    switchTab,
    deleteItem,
    addItem,
    setActiveTab,
    setIsSaving,
    setIsAdding,
    setIsDeleting,
    showPermissionsForGroup,
    requestGroupChange,
    setPermissionsCache,
    setIsPermissionsTabVisible,
    setPendingTabId,

    // Direct save methods
    registerSaveHandler,
    unregisterSaveHandler,
    executeSave,
  };
}

// ============= Provider Component =============

export function AdminStateProvider({ children }) {
  const adminState = useAdminStateManager();

  return (
    <AdminStateContext.Provider value={adminState}>
      {children}
    </AdminStateContext.Provider>
  );
}

// ============= Consumer Hook =============

export function useAdminState() {
  const context = useContext(AdminStateContext);
  if (!context) {
    throw new Error("useAdminState must be used within AdminStateProvider");
  }
  return context;
}
